import { Injectable } from '@angular/core';

const BLOB_URL_REVOCATION_DELAY_MS = 60_000;

@Injectable({
  providedIn: 'root'
})
export class FileBlobOpenerService {
  static getMimeTypeFromMetadata(metadata: string, fallbackMimeType: string): string {
    // Prefer MIME type from data URL metadata when available.
    const mimeTypeFromMetadata = new RegExp(/^data:([^;]+);base64$/).exec(metadata)?.[1];
    return !!mimeTypeFromMetadata && mimeTypeFromMetadata !== 'null' ? mimeTypeFromMetadata : fallbackMimeType;
  }
  /**
   * Converts a base64 string (with or without a data URL prefix) to a Blob object URL.
   *
   * @param base64Input  Raw base64 or a `data:<mime>;base64,<payload>` string.
   * @param fallbackMimeType  MIME type used when the input has no data URL prefix.
   */
  createBlobUrlFromBase64(base64Input: string, fallbackMimeType = 'application/pdf'): string {
    const hasDataUrlPrefix = base64Input.startsWith('data:');
    const [metadata, payload = ''] = hasDataUrlPrefix ? base64Input.split(',', 2) : ['', base64Input];
    const mimeType = FileBlobOpenerService.getMimeTypeFromMetadata(metadata, fallbackMimeType);
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: mimeType });
    return URL.createObjectURL(blob);
  }

  /**
   * Opens a base64-encoded document in a new browser tab using a Blob URL.
   * Revokes the URL automatically after a delay.
   *
   * @returns `true` when the window was opened, `false` when the popup was blocked.
   */
  openInBrowser(base64Input: string, mimeType = 'application/pdf'): boolean {
    const blobUrl = this.createBlobUrlFromBase64(base64Input, mimeType);
    const openedWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');

    // Delay revocation to ensure the browser has consumed the object URL.
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), BLOB_URL_REVOCATION_DELAY_MS);
    return !!openedWindow;
  }
}
